import { Box, Button, Checkbox, Divider, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Select, Switch, TextField, Typography } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { useEffect, useState } from "react";
import { menuPropsModal, additionalRequests, bathrooms, interiorRooms, otherAreas, getCompanyId, decodeToken } from "../../../utils";
import { useServices } from "../hooks/useServices";
import { ResidentialCalculatorArea } from "./ResidentialCalculatorArea";
import { ConfigureJobService } from "./ConfigureJobService";
import { buildResidentialPayload } from "../utils";
import { useDispatch, useSelector } from "react-redux";
import { setFormValues } from "../../../redux/features/residentialCalculatorSlice";
import { useCalculator } from "../hooks/useCalculator";
import { useAppSelector } from "../../../redux/hooks/redux";
import { selectCurrentCompany } from "../../../redux/features/user/selectors/userState.selectors";

const colors = {
  blue: { 500: "#2196f3" },
  blueGrey: { 700: "#455a64" },
};

export const ResidentialCalculator = ({ handleResidentialQuote, handleResidentialTab }) => {
  const { services, getServices } = useServices("residencial");
  const dispatch = useDispatch();

  const token = sessionStorage.getItem("token");
  const company = useAppSelector(selectCurrentCompany)
  const companyId = company?.id
  const { getCalculatorResidentialResult } = useCalculator();

  useEffect(() => {
    getServices();
  }, []);

  const savedValues = useSelector((state) => state.residentialCalculator.values);
  const [payloadPreview, setPayloadPreview] = useState(null);

  const { control, watch, handleSubmit, register, setValue, formState } = useForm({
    mode: "onChange",
    defaultValues: savedValues || {},
  });

  const watchService = watch("service");
  const selectedService = services?.find(({ workServices }) => workServices.id === watchService);
  const isFrequently = selectedService?.workServices?.name === "Regular Cleaning";
  const payloadId = selectedService?.id;
  const values = watch();

  const noServices = !services || services.length === 0;

  const onSubmit = async (formValues) => {
    const payload = buildResidentialPayload(formValues, companyId, payloadId);
    console.log("Payload:", payload);
    setPayloadPreview(payload);

    const data = await getCalculatorResidentialResult(payload);
    if (data) {
      handleResidentialQuote(data)
      handleResidentialTab(1)
    }
  };

  useEffect(() => {
    if (JSON.stringify(values) !== JSON.stringify(savedValues)) {
      dispatch(setFormValues(values));
    }
  }, [values, savedValues, dispatch]);

  return (
    <Box sx={{ p: 3, pt: 0, width: "100%" }}>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ width: "100%", bgcolor: "var(--fx-surface-base, #FFFFFF)", p: 4 }}>
        <Typography variant="subtitle1" textAlign="center">
          Residential Calculator
        </Typography>

        {/* Service */}
        <Typography mt={2}>Service Type</Typography>
        {noServices ? (
          <Typography variant="body2" sx={{ mt: 1, color: "text.secondary", fontStyle: "italic", textAlign: "center" }}>
            There are no active residential services. Activate them in <strong>Organization &gt; Manage Services</strong>.
          </Typography>
        ) : (
          <Controller
            name="service"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <FormControl fullWidth size="small" required sx={{ mt: 1 }}>
                <InputLabel>Service</InputLabel>
                <Select {...field} label="Service" MenuProps={menuPropsModal}>
                  {services.map(({ workServices }) => (
                    <MenuItem key={workServices.id} value={workServices.id}>
                      {workServices.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        )}

        {/* Área / Sqft, etc. */}
        {watchService && (
          <Box sx={{ minHeight: "100%", display: "flex" }}>
            <ResidentialCalculatorArea setValue={setValue} />
          </Box>
        )}

        <input type="hidden" {...register("latitude")} />
        <input type="hidden" {...register("longitude")} />
        <input type="hidden" {...register("inCoverage")} />
        <input type="hidden" {...register("inIgnoreArea")} />
        <input type="hidden" {...register("extraDistanceMiles")} />
        <input type="hidden" {...register("surchargeValue")} />
        <input type="hidden" {...register("ignoredAreaRate")} />

        <ConfigureJobService setValue={() => { }} getValues={() => { }} control={control} watch={watch} service={watchService} serviceOptions={services} />

        {/* Contract Details */}
        {isFrequently && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="subtitle1">Contract Details</Typography>
            <Grid container spacing={2} mt={1}>
              <Grid item xs={12} sm={4}>
                <Controller
                  name="frequency"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <FormControl fullWidth size="small">
                      <InputLabel>Frequency</InputLabel>
                      <Select {...field} label="Frequency" MenuProps={menuPropsModal}>
                        <MenuItem value="oneTime">One time</MenuItem>
                        <MenuItem value="daily">Daily</MenuItem>
                        <MenuItem value="weekly">Weekly</MenuItem>
                        <MenuItem value="monthly">Monthly</MenuItem>
                        <MenuItem value="yearly">Yearly</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <Controller name="interval" control={control} rules={{ required: true }} render={({ field }) => <TextField {...field} label="Interval" type="number" size="small" fullWidth />} />
              </Grid>
              <Grid item xs={6} sm={4}>
                <Controller name="duration" control={control} rules={{ required: true }} render={({ field }) => <TextField {...field} label="Duration" type="number" size="small" fullWidth />} />
              </Grid>
            </Grid>
          </>
        )}

        {/* Areas of Cleaning */}
        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle1">Areas of Cleaning</Typography>

        <Typography fontSize={13} mt={2} color={colors.blueGrey[700]}>
          Interior Rooms
        </Typography>
        <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
          {interiorRooms.map(({ label, name }) => (
            <Controller rules={{ required: true }} key={name} name={name} control={control} render={({ field }) => <TextField {...field} label={label} type="number" fullWidth size="small" />} />
          ))}
        </Box>

        <Typography fontSize={13} mt={2} color={colors.blueGrey[700]}>
          Bathrooms
        </Typography>
        <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
          {bathrooms.map(({ label, name }) => (
            <Controller rules={{ required: true }} key={name} name={name} control={control} render={({ field }) => <TextField {...field} label={label} type="number" fullWidth size="small" />} />
          ))}
        </Box>

        {/* Other Areas */}
        <Typography fontSize={13} mt={6} color={colors.blueGrey[700]}>
          Other Areas
        </Typography>
        <Grid container spacing={2} mt={1}>
          {otherAreas.map(({ name, label }) => (
            <Grid item xs={6} sm={6} md={3} key={name}>
              <Controller
                name={name}
                control={control}
                render={({ field }) => <FormControlLabel control={<Checkbox {...field} checked={!!field.value} />} label={label} sx={{ width: "100%", m: 0 }} />}
              />
            </Grid>
          ))}
        </Grid>

        {/* Additional Requests */}
        <Divider sx={{ my: 4 }} />
        <Typography variant="subtitle1">Additional Requests</Typography>
        <Grid container spacing={2} mt={1}>
          {additionalRequests.map(({ name, label }) => (
            <Grid item xs={6} sm={6} md={4} key={name}>
              <Controller
                name={name}
                control={control}
                render={({ field }) => <FormControlLabel control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />} label={label} sx={{ width: "100%", m: 0 }} />}
              />
            </Grid>
          ))}
        </Grid>

        {/* Pets */}
        <Typography mt={3} variant="subtitle1">
          Pets
        </Typography>
        <Box mt={2}>
          <Controller
            name="havePets"
            control={control}
            render={({ field }) => (
              <FormControlLabel control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />} label="Do you have pets?" sx={{ width: "100%", m: 0 }} />
            )}
          />
        </Box>

        {/* Submit */}
        <Box sx={{ justifyContent: "center", display: "flex" }}>
          <Button disabled={!formState.isValid} type="submit" variant="contained" sx={{ mt: 4, bgcolor: colors.blue[500], borderRadius: 3 }}>
            Calculate
          </Button>
        </Box>

        {/* Vista previa */}
        {payloadPreview && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Payload generado
            </Typography>
            <pre style={{ background: "var(--fx-surface-sunken, #F8FAFC)", padding: 12, borderRadius: 8 }}>{JSON.stringify(payloadPreview, null, 2)}</pre>
          </Box>
        )}
      </Box>
    </Box>
  );
};
